import { sumBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useDebounce } from 'usehooks-ts';
import {
  ContactMail,
  Delete,
  Email,
  FilterList,
  InfoOutlined,
  Visibility,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import api from 'lib/trpcClient';
import { createInvestmentViewLink } from 'adminConstants/routes';
import AlertDialog from 'components/AlertDialog';
import Chip from 'components/Chip';
import Dialog from 'components/Dialog';
import { FirmSelector } from 'components/FirmSelector';
import TrancheSelector from 'components/TrancheSelector';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import TextField from 'components/FormElements/AppTextInput';
import { Row } from 'components/Layout';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import Spacing from 'components/Spacing';
import Table, { FooterCell, FooterRow, TableActions } from 'components/Table';
import { startOfDay, isValid } from 'date-fns';
import { InvestmentSource } from 'further-types/investment';
import { dateToLabel, numberToCurrencyString } from 'further-ui/utils';
import { useAdvisersLeanResponse } from 'hooks/data/adviser/useAdvisers';
import {
  useInvestments,
  useSendPaymentReminder,
} from 'hooks/data/investment/useInvestments';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useGetRole } from 'hooks/ui/useGetRole';
import FilterPanel from './FilterPanel';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import {
  Api,
  CustomTag,
  InvestorTag,
  InvestmentPaymentStatus,
} from 'further-types/investment';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';

type InvestmentFilters = {
  firmId?: string;
  fundId?: string;
  fundType?: { _id: string };
  adviserId: string;
  searchByInvestorAndInvestment?: string;
  amount: [string, string];
  startDate?: string;
  endDate?: string;
  paymentStatus?: InvestmentPaymentStatus;
  customTags: Array<CustomTag>;
  investorTags: Array<InvestorTag>;
};

const totalCurrentValueTooltipText = (
  <>
    This is the combined value of all current holdings, exits and uninvested
    paid in capital. Where there are unpaid commitments, this figure may be
    materially lower than subscribed investment capital.
  </>
);

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Subscriptions', isActive: true },
];

const Investment: React.FC = () => {
  const [errorText, setErrorText] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});
  const { firmId } = useGetRole();

  const filterDisclosure = useDisclosure();
  const deleteDisclosure = useDisclosure<{ investmentId: string }>();

  const deleteMutation = api.investment.delete.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Investment deleted successfully',
      invalidations: [['investments']],
    }),
  );
  const sendAdviceFeePaymentConfirmationMutation =
    api.investment.sendAdviceFeePaymentConfirmation.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Advice fee payment confirmation sent successfully',
      }),
    );

  const { sendPaymentReminder } = useSendPaymentReminder();
  const pagination = usePagination({
    id: 'investments-table',
    orderBy: 'investmentDate',
    order: 'desc',
  });

  const defaultFilters: InvestmentFilters = {
    firmId,
    fundId: undefined,
    fundType: undefined,
    adviserId: '',
    searchByInvestorAndInvestment: undefined,
    amount: ['0', '0'],
    startDate: undefined,
    endDate: undefined,
    paymentStatus: undefined,
    customTags: [],
    investorTags: [],
  };

  const advancedFilterKeys: Array<keyof InvestmentFilters> = [
    'amount',
    'startDate',
    'endDate',
    'paymentStatus',
    'customTags',
    'investorTags',
  ];

  const {
    filters,
    handleChangeFilters,
    advancedFiltersApplied,
    resetAdvancedFilters,
  } = useFilters<InvestmentFilters>('investments-table', {
    defaultFilters,
    onFiltersChange: pagination.toFirstPage,
    advancedFilterKeys,
  });

  const filterButtonLabel = advancedFiltersApplied ? (
    <img
      src="images/customIcons/filter-applied.png"
      alt="filters applied"
      width="24px"
      height="24px"
    />
  ) : (
    <FilterList />
  );

  const handleFinish = () => {
    if (filters.startDate || filters.endDate) {
      const { isError } = validate();
      if (!isError) {
        filterDisclosure.onClose();
      }
    } else {
      filterDisclosure.onClose();
    }
  };

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const fundIdParam = qs.get('fundId');
    if (fundIdParam) {
      handleChangeFilters({
        fundId: fundIdParam,
        firmId: isSuperAdmin ? undefined : firmId,
        fundType: undefined,
        adviserId: '',
        searchByInvestorAndInvestment: '',
        amount: ['0', '0'],
        startDate: undefined,
        endDate: undefined,
        paymentStatus: undefined,
        customTags: [],
        investorTags: [],
      });
    }
  }, []);

  const advisers = useAdvisersLeanResponse({
    firmId,
    order: 'asc',
    orderBy: 'fullName',
  });

  const debouncedInvestorAndInvestmentSearch = useDebounce(
    filters.searchByInvestorAndInvestment,
    400,
  );

  const [deletePermission, updatePermission] = useHasPermissions([
    RolePermission.DeleteInvestment,
    RolePermission.EditInvestment,
  ]);
  const { isSuperAdmin } = useGetRole();

  const isEligibleForAdviceFeePaymentConfirmation = (elm: Row) => {
    const { adviserId, adviserFee, isFullyFunded } = elm;

    return !!(isFullyFunded && adviserId && adviserFee);
  };

  const params: Api.ListInvestmentsRequestQueryParams = {
    page: pagination.page.toString(),
    perPage: pagination.rowsPerPage.toString(),
    ...(filters.firmId ? { firmId: filters.firmId } : {}),
    fundId: filters.fundId,
    fundType: filters.fundType?._id,
    sortType: pagination.order ?? 'desc',
    keyName: pagination.orderBy ?? 'investmentDate',
    paymentStatus: filters.paymentStatus,
    minInvestment: filters.amount[0] ? +filters.amount[0] : undefined,
    maxInvestment: filters.amount[1] ? +filters.amount[1] : undefined,
    searchByInvestorAndInvestment: debouncedInvestorAndInvestmentSearch,
    adviserId: filters.adviserId || undefined,
    startDate: undefined,
    endDate: undefined,
    customTags: filters.customTags,
    investorTags: filters.investorTags,
  };
  if (filters.startDate && filters.endDate) {
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);

    if (startDate <= endDate && isValid(startDate) && isValid(endDate)) {
      params.startDate = startOfDay(startDate).toISOString();
      params.endDate = startOfDay(endDate).toISOString();
    }
  }
  const investmentsQuery = useInvestments({
    params,
  });

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = async () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.InvestmentExport,
      firmId: params.firmId ?? undefined,
      params,
    });
  };

  const handleSendPaymentReminder = (elm) => {
    sendPaymentReminder.mutate(elm._id);
  };

  const validate = () => {
    const errors: {
      startDate?: string;
      endDate?: string;
    } = {};
    let isError = false;
    if (!filters.startDate) {
      errors.startDate = 'Please select start date.';
      isError = true;
    }
    if (!filters.endDate) {
      errors.endDate = 'Please select end date.';
      isError = true;
    }
    setErrorText(errors);
    return {
      errors,
      isError,
    };
  };

  type Row = Api.ListInvestmentsResponse['result'][number];
  const columns = [
    {
      label: 'Investor name',
      key: 'fullName',
    },
    {
      label: 'Email',
      key: 'email',
      render: (elm: Row) => elm?.investorId?.email,
    },
    {
      label: 'Tranche',
      key: 'trancheName',
    },
    {
      label: 'Subscription date',
      key: 'investmentDate',
      render: (elm: Row) => dateToLabel(elm?.investmentDate),
    },
    {
      label: 'Subscription',
      key: 'investmentAmount',
      render: (elm: Row) => numberToCurrencyString(elm?.investmentAmount),
    },
    {
      label: (
        <Row centered spacing="xs">
          <span>Total value</span>
          <Tooltip title={totalCurrentValueTooltipText}>
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Row>
      ),
      key: 'investmentValue',
      render: (elm: Row) =>
        numberToCurrencyString(elm?.investmentBalance?.investmentValue),
    },
    {
      label: 'Payment status',
      key: 'isPaymentReceived',
      render: (elm: Row) => (
        <>
          {elm?.source === InvestmentSource.Transfer ? (
            <Chip small variant="success">
              Transfer
            </Chip>
          ) : elm?.isFullyFunded ? (
            <Chip small variant="success">
              Fully funded
            </Chip>
          ) : elm?.isPartiallyFunded ? (
            <Chip small variant="disabled">
              Partially funded
            </Chip>
          ) : (
            <Chip small variant="disabled">
              No payment
            </Chip>
          )}
        </>
      ),
    },
    {
      label: 'Adviser',
      key: 'adviser',
      render: (elm: Row) => elm.adviserId?.fullName || '-',
    },
    {
      label: 'Subscription ID',
      key: 'externalInvestmentId',
      render: (elm: Row) => elm?.externalInvestmentId || '-',
    },

    {
      label: 'Actions',
      key: '',
      sort: false,
      render: (elm: Row) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              label: 'View',
              icon: Visibility,
              link: createInvestmentViewLink(elm?._id),
              color: 'primary',
            },
            {
              visible: deletePermission,
              label: 'Delete',
              icon: Delete,
              onClick: () => handleDelete(elm),
              color: 'error',
            },
            {
              visible:
                updatePermission &&
                !elm?.isFullyFunded &&
                (!elm.investorId.disableAllEmails || !!elm.adviserId),
              label: 'Send payment reminder',
              icon: Email,
              onClick: () => handleSendPaymentReminder(elm),
              color: 'primary',
            },
            {
              visible: isEligibleForAdviceFeePaymentConfirmation(elm),
              disabled: Boolean(elm.adviceFeePaymentConfirmationSentAt),
              tooltip: elm.adviceFeePaymentConfirmationSentAt
                ? `Advice fee payment confirmation already sent on ${dateToLabel(
                    elm.adviceFeePaymentConfirmationSentAt,
                  )}`
                : undefined,
              label: 'Send advice fee payment confirmation',
              icon: ContactMail,
              onClick: () =>
                sendAdviceFeePaymentConfirmationMutation.mutate({
                  investmentId: elm._id,
                }),
              color: 'primary',
            },
          ]}
        />
      ),
    },
  ];

  const handleDelete = (rec) => {
    deleteDisclosure.stageAction({ investmentId: rec._id });
  };

  const handleConfirmDelete = async () => {
    if (deleteDisclosure.actionProps.investmentId) {
      deleteMutation.mutate({
        investmentId: deleteDisclosure.actionProps.investmentId,
      });
    }
    deleteDisclosure.onClose();
  };

  return (
    <PageContainer heading="Subscriptions" breadcrumbs={breadcrumbs}>
      <PageContent>
        <PageFilters
          pageId="investments-table"
          buttons={[
            {
              label: filterButtonLabel,
              onClick: filterDisclosure.onOpen,
              isIcon: true,
            },
            {
              label: 'Export',
              onClick: handleExport,
              disabled: !investmentsQuery?.data?.result?.length,
              loading: isExporting,
            },
          ]}
        >
          <Filter>
            <TextField
              type="search"
              name="searchByInvestorAndInvestment"
              placeholder="Search by investor name, email or subscription ID"
              fullWidth
              value={filters.searchByInvestorAndInvestment}
              onChange={(event) => {
                handleChangeFilters({
                  searchByInvestorAndInvestment: event.target.value,
                });
              }}
            />
          </Filter>
          {isSuperAdmin && (
            <Filter>
              <FirmSelector
                firmId={filters.firmId}
                placeholder="Search by firm"
                onChange={(firmId) => {
                  handleChangeFilters({ firmId });
                }}
              />
            </Filter>
          )}
          <Filter>
            <TrancheSelector
              firmId={filters.firmId}
              selectedTranche={
                filters.fundId ? { _id: filters.fundId } : undefined
              }
              clearable
              onChange={(tranche) => {
                handleChangeFilters({ fundId: tranche?._id });
              }}
            />
          </Filter>
          <Filter>
            {!advisers.isFetching ? (
              <Autocomplete
                id="adviserId"
                options={
                  advisers.data?.advisers.filter(
                    (adviser) => !!adviser.fullName,
                  ) || []
                }
                value={(advisers.data?.advisers ?? []).find(
                  (adviser) => adviser._id === filters.adviserId,
                )}
                getOptionLabel={(option) =>
                  `${option?.fullName} (${option?.email})`
                }
                getOptionKey={(option) => option._id}
                filterSelectedOptions
                onChange={(_, newValue) => {
                  handleChangeFilters({ adviserId: newValue?._id });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Adviser"
                  />
                )}
              />
            ) : (
              <TextField
                variant="outlined"
                placeholder="Adviser"
                disabled
                value="Loading..."
              />
            )}
          </Filter>
        </PageFilters>

        <Table
          columns={columns}
          onRequestSort={pagination.handleRequestSort}
          order={pagination.order}
          orderBy={pagination.orderBy}
          tablebody={investmentsQuery?.data?.result ?? []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={investmentsQuery?.data?.total ?? 0}
          pagination={true}
          loading={investmentsQuery.isFetching}
          TableFooter={
            <tfoot>
              <FooterRow>
                <FooterCell colSpan={4} />
                <FooterCell>
                  {numberToCurrencyString(
                    sumBy(investmentsQuery.data?.result, 'investmentAmount'),
                  )}
                </FooterCell>
                <FooterCell>
                  {numberToCurrencyString(
                    sumBy(
                      investmentsQuery.data?.result,
                      'investmentBalance.investmentValue',
                    ),
                  )}
                </FooterCell>
                <FooterCell />
                <FooterCell />
                <FooterCell />
                <FooterCell />
              </FooterRow>
            </tfoot>
          }
          variant="nohover"
        />
      </PageContent>

      <AlertDialog
        open={deleteDisclosure.isOpen}
        title={`This cannot be undone and all information relating to the subscription will be lost.`}
        content={
          <Spacing>
            <p>
              Any money transferred to you must be refunded to the investor
              before the subscription is deleted.
            </p>
            <p>
              Any money that was transferred from the investor's cash balance to
              pay for this subscription will be returned to their cash balance.
            </p>
          </Spacing>
        }
        size="md"
        onClose={deleteDisclosure.onClose}
        onConfirm={handleConfirmDelete}
        btnLabels={{ cancel: 'Go back', confirm: 'Delete subscription' }}
        cancelBtnProps={{ variant: 'outlined' }}
        confirmBtnProps={{ color: 'secondary' }}
      />
      <Dialog
        open={filterDisclosure.isOpen}
        title="Advanced Filters"
        onClose={filterDisclosure.onClose}
        onCancel={() => {
          resetAdvancedFilters();
          setErrorText({});
          filterDisclosure.onClose();
        }}
        onConfirm={handleFinish}
        btnLabels={{
          cancel: 'Reset',
          confirm: 'Apply',
        }}
      >
        <FilterPanel
          filters={filters}
          handleChangeFilters={handleChangeFilters}
          errorText={errorText}
        />
      </Dialog>
    </PageContainer>
  );
};

export default Investment;
